"use client";

import { createRestDataProviderClient } from "@providers/data-provider/rest-data-provider.client";
import { HighlightedText } from "@shared/ui";
import { PinLocationIcon } from "@shared/ui/custom-icon";
import { getSlug, imageFallback, transformQueryParamsWithoutLocale } from "@shared/utils";
import { Skeleton } from "@ubud-ui/react-ui-kit";
import { useQuery } from "@tanstack/react-query";
import Image from "next/image";
import Link from "next/link";

type IndustrialVacancy = {
  id: string;
  title: string;
  companyName: string;
  cityName: string;
  companyLogoUri: string;
  platformType: "internal" | "external";
  platformName: string;
  platformLogo: string;
};

export function IndustrialVacancyResult({ keyword, onClick }: { keyword: string; onClick: () => void }) {
  const restDataProviderClient = createRestDataProviderClient();

  const { data: result, isFetching } = useQuery<{ data: IndustrialVacancy[] }>({
    queryKey: ["header-search-industrial-vacancies", keyword],
    queryFn: () =>
      restDataProviderClient.custom({
        url: transformQueryParamsWithoutLocale("/karirhub/vacancy/v2/published-industrial-vacancies", {
          limit: 3,
          keyword: keyword,
        }),
        method: "GET",
      }),
    enabled: true,
  });

  if (isFetching) {
    return (
      <div>
        <h4 className="p-3 font-bold text-lg">Lowongan dalam negeri</h4>
        <div className="space-y-2 mt-1">
          {[1, 2, 3].map((it) => {
            return (
              <div className="rounded-lg relative h-10 overflow-hidden" key={it}>
                <Skeleton className="absolute w-full h-full bg-gray-200" />
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (result && result.data?.length <= 0) {
    return <></>;
  }

  return (
    <div>
      <h4 className="p-3 font-bold text-lg">Lowongan dalam negeri</h4>
      <div>
        {result?.data?.map((it) => {
          return (
            <Link
              key={it.id}
              href={`/lowongan-dalam-negeri/lowongan/${getSlug(it.id, it.title)}`}
              className="flex items-start hover:bg-gray-200 rounded-lg p-3 gap-3"
              onClick={onClick}
            >
              <Image
                src={it.companyLogoUri || "/blank-white.webp"}
                className="w-12 h-12 min-w-12 rounded-lg bg-gray-100 object-cover border border-gray-200 mt-0.5"
                width="48"
                height="48"
                alt={it.companyName}
                onError={imageFallback()}
              />
              <div className="flex-1">
                <div className="font-semibold text-base">
                  <HighlightedText keyword={keyword} text={it.title} />
                </div>
                {it.companyName && <div className="mt-1 text-sm">{it.companyName}</div>}
                {it.cityName && (
                  <div className="mt-2 text-xs flex items-start gap-1">
                    <PinLocationIcon className="size-3 min-w-3 mt-0.5" />
                    <span className="flex-1">{it.cityName}</span>
                  </div>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
